import { factState } from "./fact-state";
import type {
    Fact,
    FactCategory,
    Operations,
    Pricing,
    ProviderProfileV2,
} from "./types";

const COUNT_FORMATTER = new Intl.NumberFormat("en-US");

export function hasTrustFactsContent(
    profile: ProviderProfileV2,
    excludeCategories: FactCategory[] = [],
): boolean {
    return profile.factGroups.some(
        (group) =>
            !excludeCategories.includes(group.category) &&
            group.facts.length > 0,
    );
}

export function hasPricingContent(pricing: Pricing): boolean {
    return pricing.fees.length > 0;
}

export function hasScreeningColumnContent(
    facts: Fact[] | null | undefined,
): boolean {
    return (facts ?? []).some((fact) => factState(fact) !== "no");
}

export function hasWhatToExpectContent(signals: {
    coordinates: { lat: number; lng: number } | null | undefined;
    screeningFacts: Fact[] | null | undefined;
}): boolean {
    return (
        signals.coordinates != null ||
        hasScreeningColumnContent(signals.screeningFacts)
    );
}

export function shelterOperationsRows(
    operations: Operations,
): { label: string; value: string }[] {
    const rows: { label: string; value: string }[] = [];

    if (operations.intakeModel === "open_admission") {
        rows.push({
            label: "Takes in",
            value: "Every animal in their area (open-admission)",
        });
    } else if (operations.intakeModel === "limited_admission") {
        rows.push({ label: "Takes in", value: "Selected animals" });
    }
    if (operations.acceptsStrays) {
        rows.push({ label: "Strays", value: "Accepted" });
    }
    if (operations.strayHoldDays != null) {
        rows.push({
            label: "Stray hold",
            value: `${operations.strayHoldDays} days before adoption`,
        });
    }
    if (operations.acceptsOwnerSurrenders) {
        rows.push({ label: "Owner surrenders", value: "Accepted" });
    }
    for (const count of [
        { label: "Annual intake", value: operations.annualIntake },
        { label: "Annual adoptions", value: operations.annualAdoptions },
        { label: "Annual fosters", value: operations.annualFosters },
    ]) {
        if (count.value != null && count.value > 0) {
            rows.push({
                label: count.label,
                value: COUNT_FORMATTER.format(count.value),
            });
        }
    }
    if (operations.onsiteVet === true) {
        rows.push({ label: "On-site veterinarian", value: "Available" });
    }

    return rows;
}

export function shelterShowsAtRisk(operations: Operations): boolean {
    return operations.mayEuthanizeForSpace === true;
}

export function hasShelterOperationsContent(
    operations: Operations | null | undefined,
): boolean {
    if (!operations) return false;
    return (
        shelterOperationsRows(operations).length > 0 ||
        shelterShowsAtRisk(operations)
    );
}
